import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { DocumentTextIcon, LockClosedIcon, CheckCircleIcon, DownloadIcon } from '@heroicons/react/outline';
import { ExclamationCircleIcon } from '@heroicons/react/outline';
import { ChevronDownIcon } from '@heroicons/react/solid';
import { useNavigate } from 'react-router-dom';
import { PatientDataContext } from '../../../../context/PatientDataContext';
import { db } from '../../../../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { formatDateTime } from '../../../../utils/timestampUtils';
import { TriggerFetchLatestDocForDownload } from '../../../../utils/TriggerFetchLatestDocForDownload';
import PreAssessmentReportWizard from './PreAssessmentReportWizard';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';

/**
 * PreAssessmentDocuments Component
 * --------------------------------
 * This component renders the "Pre-Assessment Documents" panel specifically for
 * ADHD and Autism Associates organisation. It allows clinicians to generate a
 * Pre-Assessment Report that summarises patient-provided history form data
 * BEFORE the actual clinical assessment takes place.
 * 
 * Key Features:
 * - Only visible for "ADHD and Autism Associates" organisation
 * - Displays Pre-Assessment Report status and allows generation
 * - Requires all patient history forms to be completed before generation
 * - Any primary assigned clinician (CI/SSI/DD/PAD/Lead) can generate the report
 * - Links to the review page (RD_PreR) once generated
 * 
 * Design Notes:
 * - Follows the same visual pattern as AdditionalDocuments component
 * - Uses Firestore real-time listener for processing status updates
 * - Generation is triggered via the generatePreAssessmentReport cloud function
 */
export default function PreAssessmentDocuments({ patientId, clinicianId }) {
  const navigate = useNavigate();
  const { patientDataModel, fetchPatientData } = useContext(PatientDataContext);
  
  // State for the wizard dialog
  const [showWizard, setShowWizard] = useState(false);
  
  // State for download functionality
  const [isDownloading, setIsDownloading] = useState(false);
  
  /**
   * Processing Status Management
   * ---------------------------
   * Tracks the state of Pre-Assessment Report generation.
   * Possible values: 'idle', 'processing', 'complete', 'error'
   */
  const [processingStatus, setProcessingStatus] = useState('idle');
  
  // Ref to track previous processing status for transition detection
  const prevProcessingStatusRef = useRef('idle');

  /**
   * Determine if the current clinician can generate the Pre-Assessment Report.
   * 
   * IMPORTANT: Only the LEAD CLINICIAN can generate, review, and approve the 
   * Pre-Assessment Report. This restriction exists because the report template 
   * includes the clinician's signature, and we can only have a single clinician's 
   * signature appear in the document.
   */
  const canGenerateReport = useMemo(() => {
    if (!clinicianId) return false;
    
    // Check if the current user is the Lead Clinician for this patient
    const clinicians = patientDataModel?.clinicians || {};
    const leadClinician = clinicians.clinicianLead;
    
    return leadClinician?.clinicianId === clinicianId;
  }, [patientDataModel, clinicianId]);

  /**
   * Calculate Pre-Assessment Report status from the data model.
   * Uses the preARReportGenerated and preARReportApproved flags.
   */
  const reportStatus = useMemo(() => {
    const manageReports = patientDataModel?.dataModel?.['6_manageReports'] || {};
    const isGenerated = manageReports.preARReportGenerated;
    const isApproved = manageReports.preARReportApproved;
    
    if (isApproved) {
      return 'approved';
    } else if (isGenerated) {
      return 'ready-for-review';
    } else {
      return 'locked';
    }
  }, [patientDataModel]);

  /**
   * Get the assigned clinician info for display.
   * For Pre-Assessment Report, ONLY the Lead Clinician can generate/review/approve,
   * so we always show the Lead Clinician's info.
   */
  const assignedClinician = useMemo(() => {
    const clinicians = patientDataModel?.clinicians || {};
    const lead = clinicians.clinicianLead;
    
    return {
      name: lead?.clinicianName || 'No Lead Clinician assigned',
      id: lead?.clinicianId || null
    };
  }, [patientDataModel]);

  /**
   * Check if all patient history forms are completed (100% progress).
   * This is required before the Pre-Assessment Report can be generated.
   */
  const allHistoryFormsComplete = useMemo(() => {
    const formData = patientDataModel?.formData || {};
    const dataModel = patientDataModel?.dataModel || {};
    const preAppointmentInfo = dataModel['1_preAppointmentInformation'] || {};
    const sections = preAppointmentInfo.sections || {};
    
    // Find the Patient History section
    let patientHistorySection = null;
    for (const section of Object.values(sections)) {
      if (section.internalName === "Patient History") {
        patientHistorySection = section;
        break;
      }
    }
    
    if (!patientHistorySection || !patientHistorySection.domains) {
      return false;
    }
    
    // Check each domain's corresponding form completion status
    const domains = patientHistorySection.domains || {};
    for (const domain of Object.values(domains)) {
      const displayName = domain.displayName;
      
      // Find matching form in formData
      let matchingFormKey = null;
      for (const [formKey, form] of Object.entries(formData)) {
        if (form?.formTitle === displayName) {
          matchingFormKey = formKey;
          break;
        }
      }
      
      if (!matchingFormKey) {
        // Form doesn't exist - not complete
        return false;
      }
      
      const form = formData[matchingFormKey];
      const progressPercentage = form?.progress?.progressPercentage || 0;
      
      if (progressPercentage < 100) {
        return false;
      }
    }
    
    return true;
  }, [patientDataModel]);

  /**
   * Firestore Real-time Status Monitoring
   * ------------------------------------
   * Sets up a listener to monitor changes to the Pre-Assessment Report processing status.
   * This allows the UI to reflect status changes immediately during long-running operations.
   */
  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'patients', patientId),
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          const manageReports = data?.dataModel?.['6_manageReports'] || {};
          
          const newStatus = manageReports.preARReportProcessingStatus || 'idle';
          const previousStatus = prevProcessingStatusRef.current;
          
          // Update ref with new status
          prevProcessingStatusRef.current = newStatus;
          
          // Update state
          setProcessingStatus(newStatus);
          
          // If transitioning from processing to complete, refresh patient data
          if (previousStatus === 'processing' && newStatus === 'complete') {
            console.log('Pre-Assessment Report generation completed - refreshing data');
            fetchPatientData(patientId, true);
          }
        }
      },
      (error) => {
        console.error("Error in Firestore snapshot listener:", error);
        if (error.code === 'deadline-exceeded') {
          console.log("Deadline exceeded in listener - this is expected for long-running operations");
        }
      }
    );
    
    return () => unsubscribe();
  }, [patientId, fetchPatientData]);

  /**
   * UI Helper Functions
   * ------------------
   * These functions determine visual styling based on document status.
   */
  const getBackgroundClass = () => {
    if (processingStatus === 'processing') {
      return 'bg-amber-50 hover:bg-amber-100';
    }
    if (processingStatus === 'error') {
      return 'bg-red-50 hover:bg-red-100';
    }
    if (reportStatus === 'approved') {
      return 'bg-green-50 hover:bg-green-100';
    }
    if (reportStatus === 'ready-for-review') {
      return 'bg-violet-50 hover:bg-violet-100';
    }
    return 'bg-gray-50 hover:bg-gray-100';
  };

  const getCircleClass = () => {
    if (processingStatus === 'processing') {
      return 'bg-amber-50 border-amber-200 text-amber-700';
    }
    if (processingStatus === 'error') {
      return 'bg-red-50 border-red-200 text-red-700';
    }
    if (reportStatus === 'approved') {
      return 'bg-green-50 border-green-200 text-green-700';
    }
    if (reportStatus === 'ready-for-review') {
      return 'bg-violet-50 border-violet-200 text-violet-700';
    }
    return 'bg-gray-50 border-gray-200 text-gray-700';
  };

  /**
   * Status Icon Renderer
   * -------------------
   * Returns the appropriate icon based on document status and processing state.
   */
  const getStatusIcon = () => {
    if (processingStatus === 'processing') {
      // Show different message based on whether report is already approved (finalising document)
      // or still being generated (initial generation)
      const isFinalisingDocument = reportStatus === 'approved';
      const statusMessage = isFinalisingDocument ? 'Finalising...' : 'Generating...';
      
      return (
        <div className="flex items-center gap-2 text-amber-600">
          <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="text-sm font-medium">{statusMessage}</span>
        </div>
      );
    }
    
    if (processingStatus === 'error') {
      return (
        <div className="flex items-center gap-2 text-red-600">
          <ExclamationCircleIcon className="h-5 w-5" />
          <span className="text-sm font-medium">An error occurred. Please try again.</span>
        </div>
      );
    }
    
    switch (reportStatus) {
      case 'locked':
        return <LockClosedIcon className="w-5 h-5 text-gray-400" />;
      case 'ready-for-review':
        return <DocumentTextIcon className="w-5 h-5 text-violet-600" />;
      case 'approved':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      default:
        return null;
    }
  };

  /**
   * Navigation handler for the review page.
   */
  const handleReviewClick = () => {
    navigate(`/patient/${patientId}/review-document-pre-assessment-report`);
  };

  /**
   * Opens the generation wizard dialog.
   */
  const handleGenerateClick = () => {
    setShowWizard(true);
  };

  /**
   * Handle Pre-Assessment Report download.
   * Fetches the latest document from Firebase Storage and triggers browser download.
   * @param {string} format - 'pdf' or 'docx'
   */
  const handleDownloadReport = async (format) => {
    try {
      setIsDownloading(true);
      
      await TriggerFetchLatestDocForDownload({
        reportStage: "Pre-Assessment Report",
        clinicianId: clinicianId,
        patientId: patientId,
        fileFormat: format || 'pdf'
      });
      
    } catch (error) {
      console.error('Error downloading Pre-Assessment Report:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  /**
   * DownloadDropdown Component
   * -------------------------
   * A dropdown menu for selecting download format (PDF or DOCX).
   * Shows a loading spinner when download is in progress.
   */
  const DownloadDropdown = ({ onDownload, isDownloading, label = "Download" }) => (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger 
        disabled={isDownloading}
        className="bg-violet-800 text-white hover:bg-violet-700 disabled:bg-violet-400 
          flex items-center justify-center gap-1 py-2 px-4 text-sm font-semibold min-w-24 
          rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-violet-500 
          focus:ring-offset-2"
      >
        {isDownloading ? (
          <>
            <svg className="animate-spin h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Downloading...
          </>
        ) : (
          <>
            <DownloadIcon className="h-4 w-4" />
            {label}
            <ChevronDownIcon className="h-4 w-4" />
          </>
        )}
      </DropdownMenu.Trigger>
      
      <DropdownMenu.Portal>
        <DropdownMenu.Content 
          className="min-w-[120px] bg-white rounded-md shadow-lg border border-gray-200 py-1 z-50"
          sideOffset={5}
        >
          <DropdownMenu.Item
            className="px-3 py-2 text-sm text-gray-700 hover:bg-violet-100 hover:text-violet-900 cursor-pointer outline-none"
            onSelect={(e) => {
              e.preventDefault();
              onDownload('pdf');
            }}
          >
            Download PDF
          </DropdownMenu.Item>
          <DropdownMenu.Item
            className="px-3 py-2 text-sm text-gray-700 hover:bg-violet-100 hover:text-violet-900 cursor-pointer outline-none"
            onSelect={(e) => {
              e.preventDefault();
              onDownload('docx');
            }}
          >
            Download DOCX
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );

  // Get timestamp for approved reports
  const manageReports = patientDataModel?.dataModel?.['6_manageReports'] || {};
  const approvedAt = manageReports.preARReportApprovedAt;
  const generatedAt = manageReports.sections?.['9']?.generatedAt;

  return (
    <>
      <div className="my-4 max-w-7xl p-8 mx-auto text-left bg-white rounded-xl shadow-sm border transition-all duration-200">
        <div className="ml-4 mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Pre-Assessment Documents</h1>          
        </div>

        {/* Document Row */}
        <div className="relative">
          {/* Vertical connecting line */}
          <div className="absolute left-[40px] top-0 bottom-0 w-0.5 bg-gray-200"></div>

          <div className="relative">
            <div
              className={`
                w-full flex items-center gap-6 my-1 py-2 px-4 rounded-lg transition-all duration-200
                ${getBackgroundClass()}
              `}
            >
              {/* Circle with number */}
              <div className={`
                relative z-10 flex-shrink-0 w-12 h-12 rounded-full border-2 
                flex items-center justify-center font-semibold text-lg
                ${getCircleClass()}
              `}>
                1
              </div>

              {/* Document Info */}
              <div className="flex-1 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold text-gray-900">Pre-Screening Report</h3>
                    {getStatusIcon()}
                  </div>

                  {/* Reviewer/Status information */}
                  <div className="flex items-center gap-2 mt-1">
                    {reportStatus === 'approved' && (approvedAt || generatedAt) ? (
                      <p className="text-sm italic text-gray-700">
                        Last updated on {formatDateTime(approvedAt || generatedAt)}
                      </p>
                    ) : (
                      <>
                        <p className="text-sm italic text-gray-700">
                          {reportStatus === 'ready-for-review'
                            ? 'AI outputs ready for review by'
                            : 'Assigned to'}
                        </p>
                        <div className="inline-flex border items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700 transition-colors duration-200 cursor-help">
                          <span>{assignedClinician.name}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-3">
                  {/* Only show buttons when not processing */}
                  {processingStatus !== 'processing' && (
                    <>
                      {/* Locked state: Show generate button or locked message */}
                      {reportStatus === 'locked' && canGenerateReport && (
                        allHistoryFormsComplete ? (
                          <button
                            onClick={handleGenerateClick}
                            className="bg-violet-800 text-white hover:bg-violet-700 flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200"
                          >
                            Generate Report
                          </button>
                        ) : (
                          <div className="text-xs text-gray-500 italic max-w-[200px] text-right">
                            Available after all patient history forms are completed
                          </div>
                        )
                      )}

                      {/* Generated state: Show review button and optionally regenerate */}
                      {reportStatus !== 'locked' && canGenerateReport && (
                        <>
                          <button
                            onClick={handleGenerateClick}
                            className="text-gray-900 border border-gray-300 hover:bg-gray-100 flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200"
                          >
                            Re-generate
                          </button>
                          <button
                            onClick={handleReviewClick}
                            className={`
                              flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200
                              ${reportStatus === 'ready-for-review'
                                ? 'bg-violet-800 text-white hover:bg-violet-700'
                                : 'text-gray-900 border border-gray-300 hover:bg-green-200'
                              }
                            `}
                          >
                            {reportStatus === 'ready-for-review' ? 'Review AI Outputs' : 'Review Paragraphs'}
                          </button> 
                        </>
                      )}

                      {/* Download button - visible when report is approved and not currently processing/finalising */}
                      {reportStatus === 'approved' && processingStatus !== 'processing' && (
                        <DownloadDropdown
                          onDownload={handleDownloadReport}
                          isDownloading={isDownloading}
                          label="Download Report"
                        />
                      )}
                    </>
                  )}

                  {/* Error retry button */}
                  {processingStatus === 'error' && canGenerateReport && (
                    <button
                      onClick={handleGenerateClick}
                      className="bg-red-600 text-white hover:bg-red-700 flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200"
                    >
                      Try Again
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pre-Assessment Report Generation Wizard */}
      <PreAssessmentReportWizard
        open={showWizard}
        onOpenChange={setShowWizard}
        patientId={patientId}
        clinicianId={clinicianId}
        allHistoryFormsComplete={allHistoryFormsComplete}
      />
    </>
  );
}
